<template>
  <BContainer
    fluid
    class="student-dashboard py-3 pb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      :title="`Welcome, ${currentUser ? currentUser.first_name : 'Student'}`"
      :subtitle="currentUser ? `${currentUser.course} - Year ${currentUser.year_level}` : 'Track your appointments and student services.'"
      icon="fas fa-user-graduate"
      gradient="green"
    />

    <BAlert
      v-if="pendingFeedbackCount > 0"
      show
      variant="warning"
      class="mb-4 d-flex flex-column flex-sm-row align-items-start align-items-sm-center justify-content-between"
    >
      <div>
        <strong>Feedback Reminder</strong>
        <div class="small mt-1">
          {{ pendingFeedbackCount }} completed appointment(s) need your feedback.
        </div>
      </div>
      <RouterLink
        :to="{ name: 'student.feedback', query: { pendingFeedback: '1' } }"
        class="btn btn-sm btn-warning text-dark mt-2 mt-sm-0"
      >
        Submit Feedback
      </RouterLink>
    </BAlert>

    <BRow class="g-3 mb-4">
      <BCol
        v-for="item in metricCards"
        :key="item.label"
        cols="12"
        sm="6"
        lg="3"
      >
        <div class="kpi-card h-100">
          <div
            class="kpi-icon"
            :style="{ background: item.bg, color: item.color }"
          >
            <i :class="item.icon" />
          </div>
          <div>
            <h4 class="mb-0">
              {{ item.value }}
            </h4>
            <small>{{ item.label }}</small>
          </div>
        </div>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Completion Reliability
          </p>
          <h5 class="mb-0">
            {{ detailStats.completionRate }}%
          </h5>
          <small>{{ stats.completed }} completed out of {{ stats.total }} total</small>
        </div>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Service Footprint
          </p>
          <h5 class="mb-0">
            {{ detailStats.serviceTouches }}
          </h5>
          <small>complaints + lost reports + feedback records</small>
        </div>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Cancellation Rate
          </p>
          <h5 class="mb-0">
            {{ detailStats.canceledRate }}%
          </h5>
          <small>{{ detailStats.canceledCount }} canceled appointments</small>
        </div>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        lg="4"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <h6 class="fw-bold mb-3">
              Appointment Status
            </h6>
            <apexchart
              type="donut"
              height="285"
              :options="statusChartOptions"
              :series="statusChartSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>

      <BCol
        cols="12"
        lg="8"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Appointment Trend
              </h6>
              <small class="text-muted">Last 6 months</small>
            </div>
            <apexchart
              type="line"
              height="285"
              :options="trendChartOptions"
              :series="trendChartSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        lg="5"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <h6 class="fw-bold mb-3">
              Student Service Activity
            </h6>
            <apexchart
              type="bar"
              height="270"
              :options="serviceChartOptions"
              :series="serviceChartSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>

      <BCol
        cols="12"
        lg="7"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Upcoming Appointments
              </h6>
              <RouterLink
                :to="{ name: 'student.appointments' }"
                class="small fw-bold"
              >
                Manage
              </RouterLink>
            </div>
            <div
              v-if="recentAppointments.length"
              class="d-flex flex-column gap-2"
            >
              <div
                v-for="appointment in recentAppointments"
                :key="appointment.id"
                class="list-row"
              >
                <div>
                  <p class="mb-0 fw-bold small">
                    {{ appointmentConcernLabel(appointment) }}
                  </p>
                  <small class="text-muted">
                    {{ appointmentDateLabel(appointment) }}
                  </small>
                </div>
                <BBadge :variant="statusVariant(appointment.status)">
                  {{ appointment.status }}
                </BBadge>
              </div>
            </div>
            <div
              v-else
              class="empty-state"
            >
              No upcoming appointments.
            </div>
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>

    <BCard class="dash-card">
      <BCardBody>
        <h6 class="fw-bold mb-3">
          Quick Actions
        </h6>
        <BRow class="g-2">
          <BCol
            cols="12"
            md="6"
            lg="4"
          >
            <RouterLink
              :to="{ name: 'student.appointments' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-calendar-plus me-2 text-success" /> Book Appointment
            </RouterLink>
          </BCol>
          <BCol
            cols="12"
            md="6"
            lg="4"
          >
            <RouterLink
              :to="{ name: 'student.complaints' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-file-alt me-2 text-danger" /> File Complaint
            </RouterLink>
          </BCol>
          <BCol
            cols="12"
            md="6"
            lg="4"
          >
            <RouterLink
              :to="{ name: 'student.lost-and-found' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-search-location me-2 text-warning" /> Lost and Found
            </RouterLink>
          </BCol>
          <BCol
            cols="12"
            md="6"
            lg="4"
          >
            <RouterLink
              :to="{ name: 'student.violations' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-exclamation-triangle me-2 text-primary" /> View Violations
            </RouterLink>
          </BCol>
          <BCol
            cols="12"
            md="6"
            lg="4"
          >
            <RouterLink
              :to="{ name: 'student.feedback' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-comment-dots me-2 text-info" /> My Feedback
            </RouterLink>
          </BCol>
          <BCol
            cols="12"
            md="6"
            lg="4"
          >
            <RouterLink
              :to="{ name: 'student.settings' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-user-cog me-2 text-secondary" /> Account Settings
            </RouterLink>
          </BCol>
        </BRow>
      </BCardBody>
    </BCard>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import { SStudentAppointment, SStudentFeedback, SStudentComplaint, SStudentLostItem } from '@/services/student'
import AdminPageHeader from '@/components/core/AdminPageHeader'

const APPOINTMENT_STATUSES = ['for_confirmation', 'scheduled', 'completed', 'canceled']

export default {
  name: 'StudentDashboard',

  components: {
    AdminPageHeader
  },

  middleware: ['auth', 'student'],

  metaInfo: () => ({
    title: 'Student Dashboard'
  }),

  data () {
    return {
      stats: {
        upcoming: 0,
        total: 0,
        completed: 0,
        feedbackPending: 0,
        complaints: 0,
        lostReports: 0,
        feedback: 0
      },
      recentAppointments: [],
      pendingFeedbackCount: 0,
      detailStats: {
        completionRate: 0,
        serviceTouches: 0,
        canceledRate: 0,
        canceledCount: 0
      },
      isDarkMode: false,
      themeObserver: null,
      statusChartSeries: [0, 0, 0, 0],
      trendChartSeries: [{
        name: 'Appointments',
        data: [0, 0, 0, 0, 0, 0]
      }],
      serviceChartSeries: [{
        name: 'Count',
        data: [0, 0, 0, 0]
      }]
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    metricCards () {
      return [
        { label: 'Upcoming', value: this.stats.upcoming, icon: 'fas fa-calendar-check', color: '#0f766e', bg: 'rgba(20, 184, 166, 0.16)' },
        { label: 'Total Appointments', value: this.stats.total, icon: 'fas fa-history', color: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.16)' },
        { label: 'Completed', value: this.stats.completed, icon: 'fas fa-check-circle', color: '#15803d', bg: 'rgba(34, 197, 94, 0.16)' },
        { label: 'Feedback Pending', value: this.stats.feedbackPending, icon: 'fas fa-comment-dots', color: '#b45309', bg: 'rgba(245, 158, 11, 0.16)' }
      ]
    },

    statusChartOptions () {
      return {
        labels: ['For Confirmation', 'Scheduled', 'Completed', 'Canceled'],
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        legend: { position: 'bottom' },
        colors: ['#f9cfa7', '#a7c8f9', '#b8e3c2', '#f7b4bf'],
        dataLabels: {
          formatter: value => `${Math.round(value)}%`
        },
        plotOptions: {
          pie: {
            donut: {
              size: '62%'
            }
          }
        }
      }
    },

    trendChartOptions () {
      return {
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        stroke: {
          curve: 'smooth',
          width: 3
        },
        colors: ['#9bc2ff'],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.lastMonthLabels(6),
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        grid: { borderColor: this.isDarkMode ? '#334155' : '#dbeafe' },
        tooltip: { theme: this.isDarkMode ? 'dark' : 'light' }
      }
    },

    serviceChartOptions () {
      return {
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        chart: { toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6,
            distributed: true
          }
        },
        dataLabels: { enabled: false },
        xaxis: {
          categories: ['Complaints', 'Lost Reports', 'Feedback Sent', 'Feedback Pending'],
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        colors: ['#f7b4bf', '#f6c5a5', '#a9e6eb', '#c7c4fa'],
        grid: { borderColor: this.isDarkMode ? '#334155' : '#dbeafe' },
        tooltip: { theme: this.isDarkMode ? 'dark' : 'light' }
      }
    }
  },

  mounted () {
    this.syncColorMode()
    this.setupColorModeObserver()
    this.fetchDashboardData()
  },

  beforeDestroy () {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
      this.themeObserver = null
    }
  },

  methods: {
    async fetchDashboardData () {
      const appointmentPromise = SStudentAppointment.getMyAppointments({ page: 1, per_page: 250 }).catch(() => ({ data: {} }))
      const feedbackPromise = SStudentFeedback.myFeedback({ page: 1, per_page: 250 }).catch(() => ({ data: {} }))
      const complaintPromise = SStudentComplaint.myComplaints({ page: 1, per_page: 250 }).catch(() => ({ data: {} }))
      const lostItemPromise = SStudentLostItem.myReports({ page: 1, per_page: 250 }).catch(() => ({ data: {} }))

      const [appointmentsResponse, feedbackResponse, complaintsResponse, lostItemsResponse] = await Promise.all([
        appointmentPromise,
        feedbackPromise,
        complaintPromise,
        lostItemPromise
      ])

      const appointments = appointmentsResponse?.data?.data || appointmentsResponse?.data?.items || []
      const feedbackPayload = feedbackResponse?.data?.data || feedbackResponse?.data || {}
      const feedbackItems = feedbackPayload?.items || feedbackPayload?.data || []
      const complaints = complaintsResponse?.data?.data || complaintsResponse?.data?.items || []
      const lostItems = lostItemsResponse?.data?.data || lostItemsResponse?.data?.items || []

      const submittedAppointmentIds = new Set(feedbackItems.map(item => item.appointment_id))
      const completedAppointments = appointments.filter(item => item.status === 'completed')

      this.pendingFeedbackCount = completedAppointments.filter(item => !submittedAppointmentIds.has(item.id)).length

      this.stats.total = appointments.length
      this.stats.upcoming = appointments.filter(item => ['for_confirmation', 'scheduled'].includes(item.status)).length
      this.stats.completed = completedAppointments.length
      this.stats.feedbackPending = this.pendingFeedbackCount
      this.stats.complaints = complaints.length
      this.stats.lostReports = lostItems.length
      this.stats.feedback = feedbackItems.length

      const canceledCount = appointments.filter(item => item.status === 'canceled').length
      const denominator = appointments.length || 1

      this.detailStats = {
        completionRate: Math.round((this.stats.completed / denominator) * 100),
        serviceTouches: this.stats.complaints + this.stats.lostReports + this.stats.feedback,
        canceledRate: Math.round((canceledCount / denominator) * 100),
        canceledCount
      }

      this.recentAppointments = appointments
        .filter(item => ['for_confirmation', 'scheduled'].includes(item.status))
        .slice(0, 4)

      this.buildStatusSeries(appointments)
      this.buildTrendSeries(appointments)
      this.serviceChartSeries = [{
        name: 'Count',
        data: [
          this.stats.complaints,
          this.stats.lostReports,
          this.stats.feedback,
          this.stats.feedbackPending
        ]
      }]
    },

    buildStatusSeries (appointments) {
      const statusCounter = APPOINTMENT_STATUSES.reduce((acc, key) => {
        acc[key] = 0
        return acc
      }, {})

      appointments.forEach(appointment => {
        if (statusCounter[appointment.status] !== undefined) {
          statusCounter[appointment.status] += 1
        }
      })

      this.statusChartSeries = APPOINTMENT_STATUSES.map(key => statusCounter[key])
    },

    buildTrendSeries (appointments) {
      const keys = []
      const now = new Date()

      for (let index = 5; index >= 0; index--) {
        const cursor = new Date(now.getFullYear(), now.getMonth() - index, 1)
        const month = `${cursor.getMonth() + 1}`.padStart(2, '0')
        keys.push(`${cursor.getFullYear()}-${month}`)
      }

      const buckets = keys.reduce((acc, key) => {
        acc[key] = 0
        return acc
      }, {})

      appointments.forEach(appointment => {
        const dateSource = appointment?.appointment_slot?.date || appointment?.created_at
        const date = new Date(dateSource)

        if (Number.isNaN(date.getTime())) {
          return
        }

        const month = `${date.getMonth() + 1}`.padStart(2, '0')
        const key = `${date.getFullYear()}-${month}`

        if (buckets[key] !== undefined) {
          buckets[key] += 1
        }
      })

      this.trendChartSeries = [{
        name: 'Appointments',
        data: keys.map(key => buckets[key])
      }]
    },

    lastMonthLabels (count) {
      const labels = []
      const now = new Date()

      for (let index = count - 1; index >= 0; index--) {
        const cursor = new Date(now.getFullYear(), now.getMonth() - index, 1)
        labels.push(cursor.toLocaleString('default', { month: 'short' }))
      }

      return labels
    },

    appointmentConcernLabel (appointment) {
      return appointment?.appointment_concern_type?.name || appointment?.description || 'Appointment'
    },

    appointmentDateLabel (appointment) {
      const date = appointment?.appointment_slot?.date || appointment?.created_at
      const time = appointment?.appointment_slot?.start_time

      if (!date) {
        return 'No schedule available'
      }

      return time ? `${date} ${time}` : date
    },

    statusVariant (status) {
      switch (status) {
        case 'scheduled': return 'primary'
        case 'for_confirmation': return 'warning'
        case 'canceled': return 'danger'
        case 'completed': return 'success'
        default: return 'secondary'
      }
    },

    syncColorMode () {
      this.isDarkMode = document.body.classList.contains('dark')
    },

    setupColorModeObserver () {
      this.themeObserver = new MutationObserver(() => {
        this.syncColorMode()
      })

      this.themeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      })
    }
  }
}
</script>

<style scoped>
.student-dashboard {
  --panel-border: #e2e8f0;
  background: radial-gradient(circle at 0% 0%, #fefce8, #f8fafc 36%, #f0f9ff 100%);
}

:global(body.dark) .student-dashboard {
  --panel-border: #334155;
  background: radial-gradient(circle at 0% 0%, #1f2937 0%, #111827 45%, #0f172a 100%);
}

.hero-panel {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  border-radius: 1rem;
  color: #fff;
  padding: 1rem 1.15rem;
  background: linear-gradient(115deg, #0f766e 0%, #0284c7 52%, #2563eb 100%);
  box-shadow: 0 16px 30px rgba(14, 116, 144, 0.28);
}

.hero-mark {
  width: 46px;
  height: 46px;
  min-width: 46px;
  border-radius: 0.7rem;
  background: rgba(255, 255, 255, 0.24);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.hero-panel h2 {
  margin: 0;
  font-size: 1.16rem;
  font-weight: 700;
}

.hero-panel p {
  margin: 0;
  opacity: 0.9;
  font-size: 0.82rem;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #fff;
  border: 1px solid var(--panel-border);
  border-radius: 0.9rem;
  padding: 0.8rem;
}

:global(body.dark) .student-dashboard .kpi-card,
:global(body.dark) .student-dashboard .dash-card,
:global(body.dark) .student-dashboard .detail-card {
  background: #1e293b;
}

.kpi-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 0.72rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-card h4 {
  color: #0f172a;
  font-size: 1.2rem;
  font-weight: 700;
}

.kpi-card small {
  color: #94a3b8;
}

.dash-card {
  border: 1px solid var(--panel-border);
  border-radius: 1rem;
  box-shadow: 0 12px 20px rgba(15, 23, 42, 0.04);
}

:global(body.dark) .student-dashboard .dash-card {
  box-shadow: 0 14px 24px rgba(2, 6, 23, 0.35);
}

.list-row {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.65rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

:global(body.dark) .student-dashboard .list-row {
  border-color: #475569;
  background: rgba(148, 163, 184, 0.08);
}

.empty-state {
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  color: #94a3b8;
  text-align: center;
  padding: 1rem;
}

:global(body.dark) .student-dashboard .empty-state {
  border-color: #64748b;
  color: #cbd5e1;
}

.quick-link {
  border: 1px solid #e2e8f0;
  border-radius: 0.7rem;
}

:global(body.dark) .student-dashboard .quick-link {
  background: #223149;
  border-color: #475569;
  color: #e2e8f0;
}

.detail-card {
  border: 1px solid var(--panel-border);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(2px);
  padding: 0.85rem 0.95rem;
}

.detail-card h5 {
  color: #0f172a;
  font-weight: 700;
}

:global(body.dark) .student-dashboard .detail-card h5 {
  color: #e2e8f0;
}

.detail-card small {
  color: #64748b;
}

:global(body.dark) .student-dashboard .detail-card small {
  color: #bfd4f6;
}

.detail-label {
  color: #334155;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

:global(body.dark) .student-dashboard .detail-label {
  color: #dbeafe;
}

@media (min-width: 992px) {
  .hero-panel h2 {
    font-size: 1.4rem;
  }

  .hero-panel p {
    font-size: 0.92rem;
  }
}
</style>
